import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import {
  CareerRoleFullPictureResponse,
  CanonicalLessonAbComparisonFacetDto,
  CanonicalLessonResponse,
  CoverageRoleReport,
  ResourceResponse,
  RoleSpecializationFullResponse,
  SkillTopicCoverageEntry,
  SkillTopicItemDto,
  SkillWithResourcesResponse,
  VerifiedResourceCoverageReportResponse
} from '../../../../models';
import { CareerRoleAdminActionsService } from '../shared/career-role-admin-actions.service';
import { CareerRoleFullPictureStore } from '../shared/career-role-full-picture.store';
import { SkillResourcesCacheService } from '../shared/skill-resources-cache.service';
import { findCareerRoleSlugRoute } from '../shared/career-role-route.util';
import { RenameSkillDialogComponent } from '../shared/rename-skill-dialog.component';
import { UpdateSkillCategoryDialogComponent } from '../shared/update-skill-category-dialog.component';
import { RenameTopicDialogComponent } from '../shared/rename-topic-dialog.component';
import { AddProfileSkillDialogComponent } from '../shared/add-profile-skill-dialog.component';
import { CopyProfileSkillsDialogComponent } from '../shared/copy-profile-skills-dialog.component';
import {
  AddProfileTopicDialogComponent,
  TopicDependencyOption
} from '../shared/add-profile-topic-dialog.component';
import { EditProfileTopicDialogComponent } from '../shared/edit-profile-topic-dialog.component';
import { EditProfileSkillDependenciesDialogComponent } from '../shared/edit-profile-skill-dependencies-dialog.component';
import { formatLabelUpdateSummary } from '../shared/skill-label-update.util';
import {
  BatchGenerateLessonsDialogComponent,
  BatchGenerateLessonsDialogResult
} from '../shared/batch-generate-lessons-dialog.component';
import {
  BatchGenerateFullyAiDialogComponent,
  BatchGenerateFullyAiDialogResult
} from '../shared/batch-generate-fully-ai-dialog.component';
import { AdminCanonicalLessonPreviewComponent } from '../../shared/admin-canonical-lesson-preview.component';

@Component({
  selector: 'app-skills-resources-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatExpansionModule,
    MatChipsModule,
    MatIconModule,
    MatSnackBarModule,
    MatDialogModule,
    AdminCanonicalLessonPreviewComponent
  ],
  templateUrl: './skills-resources-tab.component.html',
  styleUrl: './skills-resources-tab.component.scss'
})
export class SkillsResourcesTabComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(CsrWebApiService);
  private readonly actions = inject(CareerRoleAdminActionsService);
  private readonly pictureStore = inject(CareerRoleFullPictureStore);
  private readonly resourcesCacheService = inject(SkillResourcesCacheService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly fb = inject(FormBuilder);
  private slugSubscription?: Subscription;

  private readonly slugSignal = signal('');
  readonly roleState = computed(() => {
    const slug = this.slugSignal();
    if (!slug) {
      return null;
    }
    return this.pictureStore.getState(slug) ?? null;
  });

  get slug(): string {
    return this.slugSignal();
  }

  get data(): CareerRoleFullPictureResponse | null {
    return this.roleState()?.picture ?? null;
  }

  get loading(): boolean {
    return this.roleState()?.loading ?? false;
  }

  get refreshing(): boolean {
    return this.roleState()?.refreshing ?? false;
  }

  readonly actionBusy = signal(false);
  readonly lessonsRequiringReview = signal<CanonicalLessonResponse[]>([]);
  readonly reviewLessonsLoading = signal(false);
  readonly abComparisonFacets = signal<CanonicalLessonAbComparisonFacetDto[]>([]);
  readonly previewLesson = signal<CanonicalLessonResponse | null>(null);
  readonly abComparisonsLoading = signal(false);
  readonly abComparisonsMinServes = signal(10);
  readonly packActionBusyId = signal<string | null>(null);

  readonly coverageLoading = signal(false);
  readonly coverageReport = signal<VerifiedResourceCoverageReportResponse | null>(null);
  readonly coverageByTopic = signal<Map<string, SkillTopicCoverageEntry>>(new Map());
  readonly gapsOnly = signal(false);
  readonly missingLessonsOnly = signal(false);
  readonly missingFullyAiLessonsOnly = signal(false);
  readonly populatingTopicKey = signal<string | null>(null);

  readonly validatingSkillId = signal<string | null>(null);

  readonly coverageForm = this.fb.nonNullable.group({
    targetThreshold: [2, [Validators.required, Validators.min(1), Validators.max(50)]],
    publishThreshold: [0.55, [Validators.required, Validators.min(0), Validators.max(1)]]
  });

  get isBusy(): boolean {
    return this.loading || this.actionBusy() || !!this.packActionBusyId() || !!this.populatingTopicKey();
  }

  get isRoleActive(): boolean {
    const status = this.data?.activationStatus ?? (this.data?.active ? 'active' : 'inactive');
    return status === 'active' || status === 'activating';
  }

  /** Admin-visible specializations: active or activating (hide inactive). */
  engagedSpecializations(role: CareerRoleFullPictureResponse | null | undefined): RoleSpecializationFullResponse[] {
    return (role?.specializations ?? []).filter((spec) => this.isSpecializationEngaged(spec));
  }

  isSpecializationEngaged(spec: RoleSpecializationFullResponse): boolean {
    const status = spec.activationStatus ?? (spec.active ? 'active' : 'inactive');
    return status === 'active' || status === 'activating' || spec.active;
  }

  get error(): string {
    return this.roleState()?.error ?? '';
  }

  get coverageRole(): CoverageRoleReport | null {
    const report = this.coverageReport();
    if (!report?.roles?.length || !this.slug) {
      return null;
    }
    return report.roles.find((r) => r.roleSlug === this.slug) ?? report.roles[0] ?? null;
  }

  ngOnInit(): void {
    const slugRoute = findCareerRoleSlugRoute(this.route);
    if (!slugRoute) {
      return;
    }
    this.slugSubscription = slugRoute.paramMap.subscribe((params) => {
      const nextSlug = params.get('slug') ?? '';
      this.resourcesCacheService.setActiveSlug(nextSlug);
      this.slugSignal.set(nextSlug);
      void this.load();
    });
  }

  ngOnDestroy(): void {
    this.slugSubscription?.unsubscribe();
  }

  async load(force = false): Promise<void> {
    if (!this.slug) {
      return;
    }
    if (force) {
      this.resourcesCacheService.clearAll();
    }
    const picture = await this.pictureStore.load(this.slug, {
      force,
      background: !force && !!this.data
    });
    if (!picture && this.error) {
      this.snackBar.open(this.error, 'Close', { duration: 3200 });
    }
    void this.loadCoverageReport();
    void this.loadLessonsRequiringReview();
    void this.loadAbComparisons();
  }

  async loadCoverageReport(): Promise<void> {
    if (!this.slug || this.coverageForm.invalid) {
      return;
    }
    this.coverageLoading.set(true);
    const raw = this.coverageForm.getRawValue();
    try {
      const report = await firstValueFrom(
        this.api.getVerifiedCoverageReport([this.slug], raw.targetThreshold, raw.publishThreshold)
      );
      this.coverageReport.set(report);
      this.coverageByTopic.set(this.buildCoverageMap(report));
    } catch (error) {
      this.coverageReport.set(null);
      this.coverageByTopic.set(new Map());
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 3500 });
    } finally {
      this.coverageLoading.set(false);
    }
  }

  private buildCoverageMap(
    report: VerifiedResourceCoverageReportResponse | null
  ): Map<string, SkillTopicCoverageEntry> {
    const map = new Map<string, SkillTopicCoverageEntry>();
    if (!report) {
      return map;
    }
    for (const role of report.roles) {
      for (const spec of role.specializations) {
        for (const entry of spec.entries) {
          map.set(this.coverageKey(entry.skillId, entry.topicName), entry);
          const compactKey = this.coverageCompactKey(entry.skillId, entry.topicName);
          if (compactKey && !map.has(compactKey)) {
            map.set(compactKey, entry);
          }
        }
      }
    }
    return map;
  }

  coverageKey(skillId: string, topicName: string): string {
    return `${skillId}::${topicName.trim().toLowerCase()}`;
  }

  private coverageCompactKey(skillId: string, topicName: string): string | null {
    const compact = topicName.replace(/[^a-zA-Z0-9]+/g, '').toLowerCase();
    if (!compact) {
      return null;
    }
    return `${skillId}::#${compact}`;
  }

  topicCoverage(skillId: string, topicName: string): SkillTopicCoverageEntry | null {
    const map = this.coverageByTopic();
    const direct = map.get(this.coverageKey(skillId, topicName));
    if (direct) {
      return direct;
    }
    const compact = this.coverageCompactKey(skillId, topicName);
    return compact ? map.get(compact) ?? null : null;
  }

  totalCoverageTopics(role: CoverageRoleReport): number {
    return role.specializations.reduce((sum, spec) => sum + spec.entries.length, 0);
  }

  coveredCoverageTopics(role: CoverageRoleReport): number {
    return role.specializations.reduce(
      (sum, spec) => sum + spec.entries.filter((e) => e.meetsTarget).length,
      0
    );
  }

  coveragePercent(role: CoverageRoleReport): number {
    const total = this.totalCoverageTopics(role);
    return total === 0 ? 100 : Math.round((this.coveredCoverageTopics(role) / total) * 100);
  }

  setGapsOnly(checked: boolean): void {
    this.gapsOnly.set(checked);
  }

  setMissingLessonsOnly(checked: boolean): void {
    this.missingLessonsOnly.set(checked);
  }

  setMissingFullyAiLessonsOnly(checked: boolean): void {
    this.missingFullyAiLessonsOnly.set(checked);
  }

  topicRowKey(skillId: string, topicName: string): string {
    return `${skillId}::${topicName}`;
  }

  canGapFillTopic(skillId: string, topicName: string): boolean {
    const entry = this.topicCoverage(skillId, topicName);
    if (!entry || (entry.meetsTarget && (entry.hasRequiredLessonCoverage ?? true))) {
      return false;
    }
    const name = topicName?.trim() ?? '';
    return name.length > 0 && name !== '(skill-wide)';
  }

  missingLessonTargets(skillId: string, topicName: string): string[] {
    return this.topicCoverage(skillId, topicName)?.missingLessonTargetProficiencies ?? [];
  }

  hasMissingLessonTargets(skillId: string, topicName: string): boolean {
    return this.missingLessonTargets(skillId, topicName).length > 0;
  }

  isPopulatingTopic(skillId: string, topicName: string): boolean {
    return this.populatingTopicKey() === this.topicRowKey(skillId, topicName);
  }

  private topicFiltersActive(): boolean {
    return this.gapsOnly() || this.missingLessonsOnly() || this.missingFullyAiLessonsOnly();
  }

  visibleTopics(skill: SkillWithResourcesResponse): SkillTopicItemDto[] {
    const topics = this.resolveTopics(skill);
    if (!this.topicFiltersActive()) {
      return topics;
    }
    // When coverage is not loaded yet, do not blank the graph.
    if (this.coverageByTopic().size === 0) {
      return topics;
    }
    const gapsOnly = this.gapsOnly();
    const missingLessonsOnly = this.missingLessonsOnly();
    const missingFullyAiLessonsOnly = this.missingFullyAiLessonsOnly();
    return topics.filter((topic) => {
      const entry = this.topicCoverage(skill.skill.id, topic.name);
      if (gapsOnly && entry?.meetsTarget) {
        return false;
      }
      if (missingLessonsOnly && (entry?.hasRequiredLessonCoverage ?? false)) {
        return false;
      }
      if (missingFullyAiLessonsOnly && entry?.hasFullyAiCanonicalLesson) {
        return false;
      }
      return true;
    });
  }

  skillHasVisibleTopics(skill: SkillWithResourcesResponse): boolean {
    return this.visibleTopics(skill).length > 0;
  }

  profileHasVisibleSkills(skills: SkillWithResourcesResponse[]): boolean {
    if (!this.topicFiltersActive() || this.coverageByTopic().size === 0) {
      return skills.length > 0;
    }
    return skills.some((skill) => this.skillHasVisibleTopics(skill));
  }

  private summarizeMissingLessonMilestones(skills: SkillWithResourcesResponse[]): {
    topicCount: number;
    milestoneCount: number;
  } {
    const topicKeys = new Set<string>();
    let milestoneCount = 0;
    for (const skill of skills) {
      for (const topic of this.resolveTopics(skill)) {
        const missing = this.missingLessonTargets(skill.skill.id, topic.name);
        if (!missing.length) {
          continue;
        }
        topicKeys.add(this.topicRowKey(skill.skill.id, topic.name));
        milestoneCount += missing.length;
      }
    }
    return { topicCount: topicKeys.size, milestoneCount };
  }

  private summarizeBatchScope(seniorityLevel: string, specializationSlug?: string | null): {
    topicCount: number;
    milestoneCount: number;
  } {
    const role = this.data;
    if (!role) {
      return { topicCount: 0, milestoneCount: 0 };
    }

    const skills = specializationSlug
      ? role.specializations
          ?.find((spec) => spec.slug === specializationSlug)
          ?.seniorityProfiles?.find((profile) => profile.level === seniorityLevel)
          ?.skills ?? []
      : role.seniorityProfiles?.find((profile) => profile.level === seniorityLevel)?.skills ?? [];
    return this.summarizeMissingLessonMilestones(skills);
  }

  private summarizeRoleWideMissingMilestones(): { topicCount: number; milestoneCount: number } {
    const role = this.data;
    if (!role) {
      return { topicCount: 0, milestoneCount: 0 };
    }

    const allSkills: SkillWithResourcesResponse[] = [
      ...(role.seniorityProfiles?.flatMap((profile) => profile.skills) ?? []),
      ...(this.engagedSpecializations(role).flatMap(
        (spec) => spec.seniorityProfiles?.flatMap((profile) => profile.skills) ?? []
      ) ?? [])
    ];
    return this.summarizeMissingLessonMilestones(allSkills);
  }

  private async refreshCountsAndCoverage(): Promise<void> {
    await this.pictureStore.refreshResourceCounts(this.slug);
    await this.loadCoverageReport();
  }

  async populateTopicGapFill(skillId: string, topicName: string): Promise<void> {
    if (!this.canGapFillTopic(skillId, topicName) || this.populatingTopicKey()) {
      return;
    }
    const key = this.topicRowKey(skillId, topicName);
    this.populatingTopicKey.set(key);
    try {
      await this.actions.populateSkillResourcesGapFill(
        this.slug,
        skillId,
        topicName,
        this.coverageForm.getRawValue().publishThreshold,
        () => {
          this.resourcesCacheService.clearForSkill(skillId);
          void this.refreshCountsAndCoverage();
        }
      );
    } finally {
      if (this.populatingTopicKey() === key) {
        this.populatingTopicKey.set(null);
      }
    }
  }

  async loadLessonsRequiringReview(): Promise<void> {
    this.reviewLessonsLoading.set(true);
    try {
      const lessons = await firstValueFrom(this.api.listCanonicalLessonsRequiringReview());
      this.lessonsRequiringReview.set(lessons ?? []);
    } catch {
      this.lessonsRequiringReview.set([]);
    } finally {
      this.reviewLessonsLoading.set(false);
    }
  }

  async loadAbComparisons(): Promise<void> {
    this.abComparisonsLoading.set(true);
    try {
      const response = await firstValueFrom(this.api.listCanonicalLessonAbComparisons());
      this.abComparisonFacets.set(response?.facets ?? []);
      this.abComparisonsMinServes.set(response?.minServes ?? 10);
    } catch {
      this.abComparisonFacets.set([]);
    } finally {
      this.abComparisonsLoading.set(false);
    }
  }

  async markLessonFalseAlarm(lesson: CanonicalLessonResponse): Promise<void> {
    this.actionBusy.set(true);
    try {
      await firstValueFrom(this.api.markCanonicalLessonFalseAlarm({ lessonId: lesson.id }));
      this.snackBar.open(`Marked lesson v${lesson.version} as false alarm`, 'Close', { duration: 2800 });
      await this.loadLessonsRequiringReview();
    } catch {
      this.snackBar.open('Could not mark false alarm', 'Close', { duration: 3200 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  async archiveLessonPack(pack: CanonicalLessonResponse): Promise<void> {
    if (
      !confirm(
        `Archive pack ${pack.evidencePackIndex ?? 0} for “${pack.topicName || pack.skillId}”? It will leave round-robin serving.`
      )
    ) {
      return;
    }

    this.packActionBusyId.set(pack.id);
    try {
      await firstValueFrom(this.api.archiveCanonicalLesson({ lessonId: pack.id }));
      this.snackBar.open(`Archived pack ${pack.evidencePackIndex ?? 0}`, 'Close', { duration: 2800 });
      await this.loadAbComparisons();
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 4000 });
    } finally {
      this.packActionBusyId.set(null);
    }
  }

  async regenerateLessonPack(pack: CanonicalLessonResponse): Promise<void> {
    const fromSources = pack.isFullyAiGenerated
      ? 'as a fully AI lesson (no curated sources)'
      : 'from the same sources';
    if (
      !confirm(
        `Regenerate pack ${pack.evidencePackIndex ?? 0} for “${pack.topicName || pack.skillId}” ${fromSources}? The current pack will be archived.`
      )
    ) {
      return;
    }

    this.packActionBusyId.set(pack.id);
    try {
      const next = await firstValueFrom(this.api.regenerateCanonicalLessonPack({ lessonId: pack.id }));
      this.snackBar.open(
        `Regenerated pack ${next.evidencePackIndex ?? 0} (v${next.version})`,
        'Close',
        { duration: 3200 }
      );
      await this.loadAbComparisons();
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 4000 });
    } finally {
      this.packActionBusyId.set(null);
    }
  }

  async publishLessonPack(pack: CanonicalLessonResponse): Promise<void> {
    const risk = pack.isFullyAiGenerated
      ? `\n\nRisk: this is a fully AI-generated lesson without curated source resources.${pack.riskNote ? ` ${pack.riskNote}` : ''}`
      : '';
    if (
      !confirm(
        `Publish pack ${pack.evidencePackIndex ?? 0} for “${pack.topicName || pack.skillId}”? It will enter learner assignment.${risk}`
      )
    ) {
      return;
    }

    this.packActionBusyId.set(pack.id);
    try {
      const next = await firstValueFrom(this.api.publishCanonicalLesson({ lessonId: pack.id }));
      this.snackBar.open(
        `Published pack ${next.evidencePackIndex ?? 0} (v${next.version})`,
        'Close',
        { duration: 3200 }
      );
      await this.loadAbComparisons();
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 4000 });
    } finally {
      this.packActionBusyId.set(null);
    }
  }

  private invalidateAfterMutation(): void {
    this.pictureStore.invalidate(this.slug, { refresh: true });
  }

  profileSkills(level: string, specializationSlug?: string): SkillWithResourcesResponse[] {
    if (!this.data) {
      return [];
    }
    if (specializationSlug) {
      const spec = this.data.specializations.find((s) => s.slug === specializationSlug);
      return spec?.seniorityProfiles.find((p) => p.level === level)?.skills ?? [];
    }
    return this.data.seniorityProfiles.find((p) => p.level === level)?.skills ?? [];
  }

  openAddSkillDialog(level: string, specializationSlug?: string): void {
    const availableSkills = this.profileSkills(level, specializationSlug).map((s) => ({
      id: s.skill.id,
      name: s.skill.name
    }));
    const ref = this.dialog.open(AddProfileSkillDialogComponent, {
      width: '560px',
      data: {
        roleSlug: this.slug,
        level,
        specializationSlug,
        availableSkills,
        availableTopics: this.collectTopicOptions(level, specializationSlug)
      }
    });
    ref.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this.snackBar.open(`Added ${result.skillName}`, 'Close', { duration: 3000 });
      this.invalidateAfterMutation();
    });
  }

  openCopySkillsDialog(level: string, specializationSlug?: string): void {
    const ref = this.dialog.open(CopyProfileSkillsDialogComponent, {
      width: '560px',
      data: {
        roleSlug: this.slug,
        level,
        specializationSlug
      }
    });
    ref.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      const parts = [
        `${result.copied} copied`,
        result.skipped ? `${result.skipped} skipped` : '',
        result.failed ? `${result.failed} failed` : ''
      ].filter(Boolean);
      this.snackBar.open(parts.join(', '), 'Close', { duration: 4000 });
      if (result.copied > 0 || result.skipped > 0) {
        this.invalidateAfterMutation();
      }
    });
  }

  openAddTopicDialog(
    skill: SkillWithResourcesResponse,
    level: string,
    specializationSlug?: string
  ): void {
    const ref = this.dialog.open(AddProfileTopicDialogComponent, {
      width: '560px',
      data: {
        roleSlug: this.slug,
        level,
        skillId: skill.skill.id,
        skillName: skill.skill.name,
        specializationSlug,
        availableTopics: this.collectTopicOptions(level, specializationSlug)
      }
    });
    ref.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this.snackBar.open(`Topics updated on ${result.skillName}`, 'Close', { duration: 3000 });
      this.invalidateAfterMutation();
    });
  }

  reviewEvidenceModes(): void {
    void this.router.navigate(['/admin/career-roles', this.slug, 'evidence-modes']);
  }

  openEditTopicDialog(
    skill: SkillWithResourcesResponse,
    topic: SkillTopicItemDto,
    level: string,
    specializationSlug?: string
  ): void {
    const ref = this.dialog.open(EditProfileTopicDialogComponent, {
      width: '520px',
      data: {
        roleSlug: this.slug,
        level,
        skillId: skill.skill.id,
        skillName: skill.skill.name,
        specializationSlug,
        topic,
        availableTopics: this.collectTopicOptions(level, specializationSlug)
      }
    });
    ref.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this.snackBar.open(`Updated topic on ${result.skillName}`, 'Close', { duration: 3000 });
      this.invalidateAfterMutation();
    });
  }

  openEditSkillDependenciesDialog(
    skill: SkillWithResourcesResponse,
    level: string,
    specializationSlug?: string
  ): void {
    const availableSkills = this.profileSkills(level, specializationSlug).map((s) => ({
      id: s.skill.id,
      name: s.skill.name
    }));
    const ref = this.dialog.open(EditProfileSkillDependenciesDialogComponent, {
      width: '520px',
      data: {
        roleSlug: this.slug,
        level,
        skillId: skill.skill.id,
        skillName: skill.skill.name,
        specializationSlug,
        dependsOn: skill.dependsOn ?? [],
        availableSkills
      }
    });
    ref.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this.snackBar.open(`Updated dependencies for ${result.skillName}`, 'Close', { duration: 3000 });
      this.invalidateAfterMutation();
    });
  }

  async removeTopic(
    skill: SkillWithResourcesResponse,
    topicName: string,
    level: string,
    specializationSlug?: string
  ): Promise<void> {
    if (!confirm(`Remove topic "${topicName}" from ${skill.skill.name}?`)) {
      return;
    }
    this.actionBusy.set(true);
    try {
      if (specializationSlug) {
        await firstValueFrom(
          this.api.removeCareerRoleFocusSkillTopic(
            this.slug,
            specializationSlug,
            level,
            skill.skill.id,
            topicName
          )
        );
      } else {
        await firstValueFrom(
          this.api.removeCareerRoleCoreSkillTopic(this.slug, level, skill.skill.id, topicName)
        );
      }
      this.snackBar.open('Topic removed', 'Close', { duration: 3000 });
      this.invalidateAfterMutation();
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 4000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  async removeSkill(
    skill: SkillWithResourcesResponse,
    level: string,
    specializationSlug?: string
  ): Promise<void> {
    if (
      !confirm(
        `Remove "${skill.skill.name}" from this ${level} profile only? The catalog skill stays available and may be auto-archived later if unused by all roles and learners.`
      )
    ) {
      return;
    }
    this.actionBusy.set(true);
    try {
      if (specializationSlug) {
        await firstValueFrom(
          this.api.removeCareerRoleFocusSkill(this.slug, specializationSlug, level, skill.skill.id)
        );
      } else {
        await firstValueFrom(this.api.removeCareerRoleCoreSkill(this.slug, level, skill.skill.id));
      }
      this.snackBar.open('Skill removed from profile', 'Close', { duration: 3000 });
      this.invalidateAfterMutation();
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 4000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  resourceCacheKey(skillId: string, topicName?: string): string {
    return this.resourcesCacheService.resourceCacheKey(skillId, topicName);
  }

  isTopicExpanded(skillId: string, topicName: string): boolean {
    return this.resourcesCacheService.isTopicExpanded(skillId, topicName);
  }

  toggleTopic(skillId: string, topicName: string): void {
    this.resourcesCacheService.toggleTopic(skillId, topicName);
  }

  isResourcesExpanded(skillId: string, topicName?: string): boolean {
    return this.resourcesCacheService.isResourcesExpanded(skillId, topicName);
  }

  isResourcesLoading(skillId: string, topicName?: string): boolean {
    return this.resourcesCacheService.isResourcesLoading(skillId, topicName);
  }

  getCachedResources(skillId: string, topicName?: string): ResourceResponse[] {
    return this.resourcesCacheService.getCachedResources(skillId, topicName);
  }

  topicResourceCount(skill: SkillWithResourcesResponse, topicName: string): number {
    const counts = skill.topicResourceCounts ?? [];
    const match = counts.find(
      (t) => t.topicName.localeCompare(topicName, undefined, { sensitivity: 'accent' }) === 0
    );
    if (match) {
      return match.resourceCount;
    }

    // Align with server DisplayLabelNormalizer: "APIIntegration" and "API Integration" are one facet.
    const compact = (value: string) => value.replace(/[^a-zA-Z0-9]+/g, '').toLowerCase();
    const target = compact(topicName);
    if (!target) {
      return 0;
    }
    const loose = counts.find((t) => compact(t.topicName) === target);
    return loose?.resourceCount ?? 0;
  }

  async toggleResources(skillId: string, topicName?: string): Promise<void> {
    if (this.resourcesCacheService.isResourcesExpanded(skillId, topicName)) {
      this.resourcesCacheService.collapseResources(skillId, topicName);
      return;
    }
    this.resourcesCacheService.markResourcesExpanded(skillId, topicName);
    if (!this.resourcesCacheService.beginResourcesLoad(skillId, topicName)) {
      return;
    }
    try {
      const resources = await firstValueFrom(
        this.api.getCareerRoleSkillResources(this.slug, skillId, topicName)
      );
      this.resourcesCacheService.completeResourcesLoad(skillId, topicName, resources);
    } catch (error) {
      this.resourcesCacheService.failResourcesLoad(skillId, topicName);
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 4000 });
    }
  }

  async populateSkillResources(skill: SkillWithResourcesResponse): Promise<void> {
    const topics = this.resolveTopics(skill).map((t) => t.name);
    const skillId = skill.skill.id;
    this.actionBusy.set(true);
    try {
      await this.actions.populateSkillResources(this.slug, skillId, topics, () => {
        this.resourcesCacheService.clearForSkill(skillId);
        void this.refreshCountsAndCoverage();
      });
    } finally {
      this.actionBusy.set(false);
    }
  }

  async validateSkillResources(skill: SkillWithResourcesResponse): Promise<void> {
    if (this.coverageForm.invalid) {
      return;
    }

    const skillId = skill.skill.id;
    this.validatingSkillId.set(skillId);
    try {
      const run = await firstValueFrom(
        this.api.validateCommittedResourcesForSkill(skillId, {
          publishThreshold: this.coverageForm.getRawValue().publishThreshold
        })
      );
      this.actions.trackExternalRun(
        run,
        `Validate resources: ${skill.skill.name}`,
        this.slug,
        () => {
          this.resourcesCacheService.clearForSkill(skillId);
          void this.refreshCountsAndCoverage();
          void this.loadLessonsRequiringReview();
        }
      );
      this.snackBar.open(`Validation queued for ${skill.skill.name}`, 'Close', { duration: 3000 });
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 4000 });
    } finally {
      this.validatingSkillId.set(null);
    }
  }

  async generateLesson(skillId: string, topicName: string, targetProficiency?: string | null): Promise<void> {
    this.actionBusy.set(true);
    try {
      const lesson = await firstValueFrom(
        this.api.generateCanonicalLesson({
          skillId,
          topicName,
          targetProficiency: targetProficiency || undefined,
          publishImmediately: true,
          maxEvidenceResources: 5
        })
      );
      const packLabel =
        lesson.evidencePackIndex != null ? ` pack ${lesson.evidencePackIndex}` : '';
      this.snackBar.open(
        `Lesson${packLabel} v${lesson.version} generated${targetProficiency ? ` for ${targetProficiency}` : ''} (${lesson.status})`,
        'Close',
        { duration: 4000 }
      );
      await this.loadCoverageReport();
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 4000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  async generateFullyAiLesson(skillId: string, topicName: string, targetProficiency?: string | null): Promise<void> {
    if (
      !confirm(
        `Generate a fully AI lesson for “${topicName}”${targetProficiency ? ` at ${targetProficiency}` : ''} with no curated resources?\n\n` +
          'This creates a draft for your review. Content is provisional — verify critical facts before publishing.\n\n' +
          'Continue?'
      )
    ) {
      return;
    }

    this.actionBusy.set(true);
    try {
      const lesson = await firstValueFrom(
        this.api.generateCanonicalLesson({
          skillId,
          topicName,
          targetProficiency: targetProficiency || undefined,
          publishImmediately: false,
          allowFullyAiGenerated: true
        })
      );
      const packLabel =
        lesson.evidencePackIndex != null ? ` pack ${lesson.evidencePackIndex}` : '';
      this.snackBar.open(
        `Fully AI lesson${packLabel} v${lesson.version} created${targetProficiency ? ` for ${targetProficiency}` : ''} as ${lesson.status}. Use Insights to review, then Publish.`,
        'Close',
        { duration: 6000 }
      );
      await this.loadCoverageReport();
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 5000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  async showLessonPacks(skillId: string, topicName: string): Promise<void> {
    try {
      const packs = await firstValueFrom(this.api.listCanonicalLessonsByFacet(skillId, topicName));
      if (packs.length === 0) {
        this.snackBar.open('No lesson packs for this topic yet.', 'Close', { duration: 3000 });
        return;
      }

      const draft = packs.find((p) => p.status === 'draft');
      if (draft) {
        const label = draft.isFullyAiGenerated ? 'fully AI draft' : 'draft';
        if (
          confirm(
            `${packs.length} pack(s) for “${topicName}”. Found a ${label} (pack ${draft.evidencePackIndex ?? 0}, v${draft.version}).\n\n` +
              (draft.riskNote ? `${draft.riskNote}\n\n` : '') +
              'Publish this draft now?'
          )
        ) {
          await this.publishLessonPack(draft);
          return;
        }
      }

      const summary = packs
        .map((p) => {
          const eng = p.engagementSignals;
          const fb = p.feedbackSummary;
          const ai = p.isFullyAiGenerated ? ', Fully AI' : '';
          return `pack ${p.evidencePackIndex ?? 0} (${p.status}${ai}): conf=${(p.confidenceScore ?? 0).toFixed(2)}, complete=${fb?.completionSignals ?? 0}, notHelpful=${fb?.notHelpfulVotes ?? 0}, score=${(eng?.learnerFeedbackScore ?? 0).toFixed(2)}`;
        })
        .join(' | ');
      this.snackBar.open(`${packs.length} pack(s) — ${summary}`, 'Close', { duration: 8000 });
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 4000 });
    }
  }

  openRenameSkillDialog(skillId: string, currentName: string): void {
    const ref = this.dialog.open(RenameSkillDialogComponent, {
      width: '420px',
      data: { skillId, currentName }
    });
    ref.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this.snackBar.open(formatLabelUpdateSummary(result), 'Close', { duration: 4500 });
      this.invalidateAfterMutation();
    });
  }

  openUpdateCategoryDialog(skillId: string, currentCategory: string): void {
    const ref = this.dialog.open(UpdateSkillCategoryDialogComponent, {
      width: '420px',
      data: { skillId, currentCategory }
    });
    ref.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this.snackBar.open(`Category updated to "${result.category}"`, 'Close', { duration: 4500 });
      this.invalidateAfterMutation();
    });
  }

  async toggleFundamental(
    skillItem: SkillWithResourcesResponse,
    _level: string,
    specializationSlug?: string
  ): Promise<void> {
    const newValue = !skillItem.isFundamental;
    skillItem.isFundamental = newValue;
    try {
      await firstValueFrom(
        this.api.patchCareerRoleProfileSkillFundamental(this.slug, skillItem.skill.id, {
          isFundamental: newValue,
          specializationSlug: specializationSlug ?? null
        })
      );
      this.snackBar.open(
        newValue ? 'Marked as fundamental skill' : 'Removed fundamental mark',
        'Close',
        { duration: 3000 }
      );
    } catch {
      skillItem.isFundamental = !newValue;
      this.snackBar.open('Failed to update fundamental flag', 'Close', { duration: 4000 });
    }
  }

  openRenameTopicDialog(skillId: string, currentName: string): void {
    const ref = this.dialog.open(RenameTopicDialogComponent, {
      width: '420px',
      data: { skillId, currentName }
    });
    ref.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this.snackBar.open(formatLabelUpdateSummary(result), 'Close', { duration: 4500 });
      this.invalidateAfterMutation();
    });
  }

  async archiveResource(resource: ResourceResponse, skillId: string, topicName?: string): Promise<void> {
    if (
      !confirm(
        `Archive "${resource.title}"? It will become purge-eligible after 30 days if unused by active learning plans.`
      )
    ) {
      return;
    }

    this.actionBusy.set(true);
    try {
      await firstValueFrom(this.api.archiveResource(resource.id));
      this.snackBar.open('Resource archived', 'Close', { duration: 3000 });
      this.resourcesCacheService.clearResourceKey(skillId, topicName);
      await this.refreshCountsAndCoverage();
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 4000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  async backfillLessonTargetProficiency(): Promise<void> {
    if (!this.slug) {
      return;
    }

    if (
      !confirm(
        'Classify published lessons missing targetProficiency for this role using AI? Queues a background job in chunks of 500 and auto-continues until done. Does not regenerate lesson content.'
      )
    ) {
      return;
    }

    this.actionBusy.set(true);
    try {
      await this.actions.backfillLessonTargetProficiency(this.slug, () => {
        void this.loadCoverageReport();
      });
      this.snackBar.open('Lesson proficiency backfill queued.', 'Close', { duration: 4000 });
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 5000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  async batchGenerateFullyAiLessons(): Promise<void> {
    if (!this.slug || !this.isRoleActive) {
      this.snackBar.open('Role must be active to generate fully AI lessons.', 'Close', { duration: 4000 });
      return;
    }

    const ref = this.dialog.open(BatchGenerateFullyAiDialogComponent, {
      width: '520px',
      data: {
        roleSlug: this.slug,
        ...this.summarizeRoleWideMissingMilestones()
      }
    });
    const result = await firstValueFrom(ref.afterClosed()) as BatchGenerateFullyAiDialogResult | undefined;
    if (!result) {
      return;
    }

    this.actionBusy.set(true);
    try {
      const summary = this.summarizeRoleWideMissingMilestones();
      await this.actions.batchGenerateFullyAiLessons(this.slug, result.forceRegenerate, () => {
        this.pictureStore.invalidate(this.slug, { refresh: true });
      });
      this.snackBar.open(
        `Fully AI lesson batch queued for ${summary.milestoneCount} missing milestone(s).`,
        'Close',
        { duration: 4000 }
      );
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 5000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  async openLessonPreview(pack: CanonicalLessonResponse): Promise<void> {
    this.actionBusy.set(true);
    try {
      const full = await firstValueFrom(this.api.getCanonicalLesson(pack.id));
      this.previewLesson.set(full);
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 4000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  closeLessonPreview(): void {
    this.previewLesson.set(null);
  }

  async publishPreviewLesson(lesson: CanonicalLessonResponse): Promise<void> {
    await this.publishLessonPack(lesson);
    this.previewLesson.set(null);
  }

  async archivePreviewLesson(lesson: CanonicalLessonResponse): Promise<void> {
    await this.archiveLessonPack(lesson);
    this.previewLesson.set(null);
  }

  async batchGenerateLessons(seniorityLevel: string, specializationSlug?: string | null): Promise<void> {
    if (!this.slug) {
      return;
    }

    const dialogRef = this.dialog.open(BatchGenerateLessonsDialogComponent, {
      width: '480px',
      data: {
        seniorityLevel,
        specializationSlug,
        ...this.summarizeBatchScope(seniorityLevel, specializationSlug)
      }
    });
    const result = await firstValueFrom(dialogRef.afterClosed()) as
      | BatchGenerateLessonsDialogResult
      | undefined;
    if (!result) {
      return;
    }

    this.actionBusy.set(true);
    try {
      const summary = this.summarizeBatchScope(seniorityLevel, specializationSlug);
      const response = await this.actions.batchGenerateLessons(
        this.slug,
        seniorityLevel,
        specializationSlug,
        () => {
          this.pictureStore.invalidate(this.slug, { refresh: true });
        },
        result.forceRegenerate
      );
      const mode = result.forceRegenerate ? 'force regenerate' : 'skip existing';
      const scope = specializationSlug
        ? `${seniorityLevel} / ${specializationSlug} (focus)`
        : `${seniorityLevel} (core)`;
      this.snackBar.open(
        `Batch lessons queued for ${scope} [${mode}] (run ${response.id}) targeting ${summary.milestoneCount} missing milestone(s).`,
        'Close',
        { duration: 4000 }
      );
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 4000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  async forceGenerateAllLessons(seniorityLevel: string, specializationSlug?: string | null): Promise<void> {
    if (!this.slug) {
      return;
    }

    const scope = specializationSlug
      ? `${seniorityLevel} / ${specializationSlug} (focus)`
      : `${seniorityLevel} (core)`;
    if (
      !confirm(
        `Force generate all lessons for ${scope}?\n\n` +
          'This archives existing lesson packs and regenerates every skill/topic facet, including milestone lesson variants.\n' +
          'Topics with resources use up to 5 evidence sources per lesson.\n' +
          'Topics with zero resources get fully AI-generated lessons.\n\n' +
          'Continue?'
      )
    ) {
      return;
    }

    this.actionBusy.set(true);
    try {
      const response = await this.actions.forceGenerateAllLessons(
        this.slug,
        seniorityLevel,
        specializationSlug,
        () => {
          this.pictureStore.invalidate(this.slug, { refresh: true });
        }
      );
      this.snackBar.open(
        `Force generate all lessons queued for ${scope} (run ${response.id}).`,
        'Close',
        { duration: 4000 }
      );
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 4000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  trackBySkill(_index: number, skill: SkillWithResourcesResponse): string {
    return skill.skill.id;
  }

  trackByResource(_index: number, resource: ResourceResponse): string {
    return resource.id;
  }

  trackByTopic(_index: number, topic: SkillTopicItemDto): string {
    return topic.name;
  }

  resolveTopics(skill: SkillWithResourcesResponse): SkillTopicItemDto[] {
    const facets = skill.topics;
    if (!facets?.length) {
      return [];
    }
    const byName = new Map<string, SkillTopicItemDto>();
    for (const f of facets) {
      if (typeof f === 'string') {
        const name = f.trim();
        if (!name || this.isSeniorityToken(name)) {
          continue;
        }
        byName.set(name.toLowerCase(), {
          name,
          level: skill.requiredProficiency,
          importance: skill.importance
        });
        continue;
      }
      const name = f.name?.trim();
      if (!name || this.isSeniorityToken(name)) {
        continue;
      }
      byName.set(name.toLowerCase(), f);
    }
    return [...byName.values()].sort((a, b) => (b.importance ?? 0) - (a.importance ?? 0));
  }

  formatDependsOn(skill: SkillWithResourcesResponse): string {
    const deps = skill.dependsOn ?? [];
    if (!deps.length) {
      return 'none';
    }
    const nameById = new Map<string, string>();
    for (const profileSkill of this.data?.seniorityProfiles.flatMap((p) => p.skills) ?? []) {
      nameById.set(profileSkill.skill.id, profileSkill.skill.name);
    }
    for (const spec of this.data?.specializations ?? []) {
      for (const profile of spec.seniorityProfiles) {
        for (const profileSkill of profile.skills) {
          nameById.set(profileSkill.skill.id, profileSkill.skill.name);
        }
      }
    }
    return deps.map((d) => nameById.get(d.skillId) ?? d.skillId).join(', ');
  }

  formatTopicDependsOn(topic: SkillTopicItemDto, level?: string, specializationSlug?: string): string {
    const deps = topic.dependsOn ?? [];
    if (!deps.length) {
      return 'none';
    }
    const options = level ? this.collectTopicOptions(level, specializationSlug) : [];
    return deps
      .map((d) => {
        const skillId = d.skillId?.trim() || '';
        const match = options.find(
          (o) =>
            o.topicName === d.topicName &&
            (!skillId || o.skillId === skillId)
        );
        return match?.label ?? (skillId ? `${skillId} / ${d.topicName}` : d.topicName);
      })
      .join(', ');
  }

  private collectTopicOptions(level: string, specializationSlug?: string): TopicDependencyOption[] {
    const options: TopicDependencyOption[] = [];
    for (const skill of this.profileSkills(level, specializationSlug)) {
      for (const topic of this.resolveTopics(skill)) {
        options.push({
          skillId: skill.skill.id,
          skillName: skill.skill.name,
          topicName: topic.name,
          label: `${skill.skill.name} / ${topic.name}`
        });
      }
    }
    return options;
  }

  private isSeniorityToken(name: string): boolean {
    const reserved = ['entry', 'junior', 'mid', 'senior', 'executive', 'principal'];
    return reserved.includes(name.trim().toLowerCase());
  }
}
